import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import FabricCard from "@/components/FabricCard";
import ZoomImage from "@/components/ZoomImage";
import WishlistButton from "@/components/WishlistButton";

const METER_PRESETS = [1, 1.25, 1.5, 1.75, 2];

export default function FabricDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [img, setImg] = useState(0);
  const [meters, setMeters] = useState(1);
  const cart = useCart();
  const { user } = useAuth();

  useEffect(() => {
    setImg(0);
    api.get(`/fabrics/${id}`).then(({ data }) => setData(data)).catch(() => setData({ notFound: true }));
  }, [id]);

  if (!data) return <div className="sq-container py-20">Loading…</div>;
  if (data.notFound) return <div className="sq-container py-20">Fabric not found.</div>;

  const f = data.fabric;
  const rate = f.price_per_meter || 0;
  const total = rate * meters;

  const onAdd = async () => {
    if (!user) { toast("Sign in to add to cart"); navigate("/auth?redirect=" + encodeURIComponent(window.location.pathname)); return; }
    try {
      await cart.add(f.id, { quantity: meters, color: f.colors?.[0] });
      toast.success(`Added ${meters} m of ${f.name}`);
    } catch (e) { toast.error("Could not add"); }
  };

  return (
    <main className="sq-container pt-10" data-testid={`fabric-detail-${f.id}`}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <div className="relative aspect-square overflow-hidden bg-[#F5F5F5]">
            <ZoomImage src={f.images?.[img]} alt={f.name} className="w-full h-full" />
            <WishlistButton id={f.id} className="absolute top-3 right-3 z-10" />
          </div>
          {f.images?.length > 1 && (
            <div className="grid grid-cols-5 gap-3 mt-3">
              {f.images.slice(0, 5).map((src, i) => (
                <button key={i} data-testid={`fabric-thumb-${i}`} onClick={() => setImg(i)} className={`aspect-square overflow-hidden border ${img === i ? "border-squire-jeans" : "border-transparent"}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="sq-eyebrow">{f.id} · {f.fabric_type || "Fabric"}</div>
          <h1 className="font-display text-4xl md:text-6xl mt-3 leading-[0.95]">{f.name}</h1>
          <div className="font-display text-3xl mt-6">{formatINR(rate)} <span className="text-base text-[#4B5563]">/ metre</span></div>
          {f.description && <p className="text-[#4B5563] mt-4 max-w-lg">{f.description}</p>}

          {f.colors?.length > 0 && (
            <div className="mt-6">
              <div className="sq-eyebrow">Colour</div>
              <div className="flex gap-2 mt-2">
                {f.colors.map((c) => (
                  <span key={c} className="px-4 py-2 text-xs uppercase tracking-[0.2em] border border-black/30">{c}</span>
                ))}
              </div>
            </div>
          )}

          {/* Metre presets — auto price */}
          <div className="mt-6">
            <div className="sq-eyebrow">Choose Length</div>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="fabric-meter-presets">
              {METER_PRESETS.map((m) => (
                <button
                  key={m}
                  data-testid={`fabric-meters-${m}`}
                  onClick={() => setMeters(m)}
                  className={`px-4 py-3 text-sm border text-center ${meters === m ? "border-black bg-black text-white" : "border-black/30 hover:border-black"}`}
                >
                  <div>{m} m</div>
                  <div className={`text-[11px] mt-1 ${meters === m ? "text-white/70" : "text-[#4B5563]"}`}>{formatINR(rate * m)}</div>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3 mt-4">
              <div className="flex items-center border border-black/30">
                <button data-testid="fabric-meters-dec" onClick={() => setMeters((m) => Math.max(0.25, +(m - 0.25).toFixed(2)))} className="px-3 py-2">−</button>
                <input
                  data-testid="fabric-meters-input"
                  type="number"
                  step="0.25"
                  min="0.25"
                  value={meters}
                  onChange={(e) => setMeters(Math.max(0.25, parseFloat(e.target.value) || 0.25))}
                  className="w-16 text-center bg-transparent outline-none"
                />
                <button data-testid="fabric-meters-inc" onClick={() => setMeters((m) => +(m + 0.25).toFixed(2))} className="px-3 py-2">+</button>
              </div>
              <span className="text-sm text-[#4B5563]">{meters} m × {formatINR(rate)} = <strong className="text-black" data-testid="fabric-total">{formatINR(total)}</strong></span>
            </div>
          </div>

          <div className="mt-8">
            <button data-testid="fabric-add-to-cart" onClick={onAdd} className="sq-btn-primary">Add {meters} m to Cart · {formatINR(total)}</button>
          </div>
        </div>
      </div>

      {data.similar?.length > 0 && (
        <section className="mt-20">
          <div className="sq-eyebrow">More in {f.fabric_type}</div>
          <h2 className="font-display text-3xl mt-2 mb-8">Similar fabrics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {data.similar.slice(0, 4).map((s) => <FabricCard key={s.id} fabric={s} />)}
          </div>
        </section>
      )}
    </main>
  );
}
